'use client';

/**
 * Barre de contrôles de la recherche d'entités
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'affichage du compteur de résultats
 *
 * @module EntityControlsBar
 */

import { useTranslations } from 'next-intl';
import { Stats } from 'react-instantsearch';

import type { EntityType } from '@/lib/config/SearchEngine/entityFields';

import { EntitySortBy } from '../widgets/EntitySortBy';

// ============================================================================
// Types
// ============================================================================

export interface EntityControlsBarProps {
  /**
   * Nom de l'index
   */
  indexName: string;
  /**
   * Type d'entité
   */
  entityType: EntityType;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Barre de contrôles pour la recherche d'entités
 *
 * Contient:
 * - Compteur de résultats
 * - Widget de tri
 *
 * @example
 * ```tsx
 * <EntityControlsBar indexName="auteurs" entityType="auteurs" />
 * ```
 */
export function EntityControlsBar({
  indexName,
  entityType,
}: EntityControlsBarProps) {
  const t = useTranslations('EntitySearch.ControlsBar');

  return (
    <div className="flex flex-col gap-4 border-b border-gray-200 py-4 lg:flex-row lg:items-center lg:justify-between">
      {/* Compteur de résultats avec composant Stats au lieu du hook */}
      <div className="ml-auto text-sm text-gray-600 lg:ml-0">
        <Stats
          translations={{
            rootElementText({ nbHits }) {
              return `${nbHits} ${nbHits === 1 ? t('result_singular') : t('result_plural')}`;
            },
          }}
          classNames={{
            root: '[&>span:first-child]:font-semibold [&>span:first-child]:text-gray-900',
          }}
        />
      </div>

      {/* Widget de tri */}
      <EntitySortBy entityType={entityType} indexName={indexName} />
    </div>
  );
}
